import { Injectable, Renderer2 } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Leg } from '@approot/models';
import { Utilities } from '@approot/utilities';
import { getAllAttributes } from '../utilities/leg-attribute-utilities';
import { scheduleLegAttributeChange } from '../utilities/leg.utilities';
import {
  calculateNotionalFromTotalNotional,
  counterNotionalOrPair,
  onCounterNotionalChange,
  updateTotalNotional
} from '../utilities/notional-utilities';
import { LegHelperService } from './leg-helper.service';
import { LegNotionalService } from './leg-notional.service';

@Injectable({
  providedIn: 'root'
})
export class LegNotionalListenersService {
  constructor(
    private legHelperService: LegHelperService,
    private legNotionalService: LegNotionalService,
    private renderer: Renderer2
  ) {}

  public notionalListeners(): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    ['', '1', '2'].forEach(notionalNumber => {
      this.genericNotionalListener(notionalNumber, form);
      this.genericCounterNotionalListener(notionalNumber);
    });

    this.totalNotionalListener();
    this.secondNotionalListener(form);
    this.notionalWithScheduleListener();
    this.genericAmountListener('putAmount', 'putCurrency', form);
    this.genericAmountListener('instalmentAmount', 'instalmentCurrency', form);
    this.genericAmountListener('nearAmount', 'currency', form);
    this.genericAmountListener('farAmount', 'currency', form);
    this.genericAmountListener('capLossAmount', 'capLossCurrency', form);
    this.genericAmountListener(
      'fixedAmountLowerEkiZone',
      'fixedAmountCurrency',
      form
    );
    this.genericAmountListener(
      'fixedAmountUpperEkiZone',
      'fixedAmountCurrency',
      form
    );
    this.currencyAttributeHandler(leg, form);
  }

  private currencyAttributeHandler(leg: Leg, form: FormGroup<any>): void {
    getAllAttributes(leg.attributesAsFormControls)
      .filter(attr => attr.currencyAttribute)
      .forEach(attr =>
        this.genericAmountListener(attr.key, attr.currencyAttribute, form)
      );
  }

  private totalNotionalListener(): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const notionalCtrl = form.get('notional');
    this.legHelperService.subSink.sink = form
      .get('totalNotional')
      ?.valueChanges.subscribe(val => {
        const notionalToAdd = counterNotionalOrPair(form, leg);
        Utilities.endOfTextBoxAdder(
          this.legHelperService.qs('#totalNotional'),
          form.get('totalNotional'),
          ` ${notionalToAdd}`,
          !!notionalToAdd,
          'currency',
          this.renderer
        );
        if (notionalCtrl && this.legHelperService.presetsLoaded) {
          if (val === '') {
            notionalCtrl.setValue('', { emitEvent: false });
          } else {
            calculateNotionalFromTotalNotional(form);
            this.legNotionalService.onNotionalChange(notionalCtrl.value, '');
            notionalCtrl.setErrors(null);
          }
        }
      });
  }

  private genericNotionalListener(
    notionalNumber: string,
    form: FormGroup
  ): void {
    // constantly listen for notional changes if a notional exist
    this.legHelperService.subSink.sink = form
      .get('notional' + notionalNumber)
      ?.valueChanges.subscribe(val => {
        this.legNotionalService.onNotionalChange(val, notionalNumber);
        if (notionalNumber === '') {
          updateTotalNotional(
            form,
            this.legHelperService.leg,
            this.legHelperService.presetsLoaded
          );
        }
      });
  }

  private notionalWithScheduleListener(): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    ['notionalLong', 'notionalShort', 'callNotional', 'putNotional'].forEach(
      notional => {
        // constantly listen for notional changes if a notional exist
        this.legHelperService.subSink.sink = form
          .get(notional)
          ?.valueChanges.subscribe(_ => {
            const notionalToAdd = counterNotionalOrPair(form, leg);
            Utilities.endOfTextBoxAdder(
              this.legHelperService.qs('#' + notional),
              form.get(notional),
              ' ' + notionalToAdd,
              !!notionalToAdd,
              'currency',
              this.renderer
            );
            scheduleLegAttributeChange(form, leg, notional, 'schedule');
          });
      }
    );
  }

  private secondNotionalListener(form: FormGroup): void {
    const leg = this.legHelperService.leg;
    // constantly listen for notional changes if a notional exist
    this.legHelperService.subSink.sink = form
      .get('secondNotional')
      ?.valueChanges.subscribe(_ => {
        if (form.get('secondCurrency').value) {
          Utilities.endOfTextBoxAdder(
            this.legHelperService.qs('#' + 'secondNotional'),
            form.get('secondNotional'),
            ' ' + form.get('secondCurrency').value,
            !!form.get('secondCurrency').value,
            'currency',
            this.renderer
          );
        }
        scheduleLegAttributeChange(form, leg, 'secondNotional', 'schedule');
      });
  }

  private genericAmountListener(
    amount: string,
    currency: string,
    form: FormGroup
  ): void {
    // listens to changes in an amount attribute and adds the corresponding currency to the end of it
    this.legHelperService.subSink.sink = form
      .get(amount)
      ?.valueChanges.subscribe(_ => {
        const currencyToAdd = form.get(currency)
          ? form.get(currency).value
          : '';
        Utilities.endOfTextBoxAdder(
          this.legHelperService.qs('#' + amount),
          form.get(amount),
          ' ' + currencyToAdd,
          !!currencyToAdd,
          'currency',
          this.renderer
        );
      });
  }

  private genericCounterNotionalListener(counterNotionalNumber: string): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    if (form.get('strike' + counterNotionalNumber)) {
      this.legHelperService.subSink.sink = form
        .get('counterNotional' + counterNotionalNumber)
        ?.valueChanges.subscribe(val => {
          onCounterNotionalChange(
            val,
            counterNotionalNumber,
            form,
            this.legHelperService.presetsLoaded,
            leg,
            rfqExecutionSettings,
            this.legHelperService.qs('#' + 'notional' + counterNotionalNumber),
            this.legHelperService.qs(
              '#' + 'counterNotional' + counterNotionalNumber
            ),
            this.renderer
          );
        });
    }
  }
}
